import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error in Admin Panel:', error, errorInfo);
  }

  public override render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.logo}>
              <span style={styles.logoText}>ZARIN</span>
              <span style={styles.logoSub}>SYSTEM NOTICE</span>
            </div>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              {this.state.error?.message || 'An unexpected error occurred in the administrative interface.'}
            </p>
            <div style={styles.actions}>
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                style={styles.retryBtn}
              >
                Reload Application
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#161616',
    padding: 24,
    color: '#F0EDE8',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  card: {
    maxWidth: 480,
    width: '100%',
    background: '#1E1E1E',
    border: '1px solid #333333',
    borderRadius: 12,
    padding: 36,
    textAlign: 'center',
  },
  logo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: 24,
  },
  logoText: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: 26,
    letterSpacing: '0.2em',
    color: '#F0EDE8',
  },
  logoSub: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.35em',
    color: '#EF5350',
    marginTop: 4,
  },
  title: {
    fontSize: 18,
    fontWeight: 600,
    marginBottom: 8,
  },
  message: {
    fontSize: 13,
    color: '#9B9590',
    marginBottom: 24,
    lineHeight: 1.6,
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
  },
  retryBtn: {
    padding: '10px 24px',
    background: '#C9A84C',
    color: '#1A1A1A',
    fontWeight: 600,
    fontSize: 13,
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
  },
};
